<template>
  <component
    :is="props.route ? 'router-link' : props.static ? 'div' : 'button'"
    v-bind="componentProps"
    class="nav-item"
    :class="{ 
      active: isCurrentRoute,
      'nav-item--danger': props.type === 'danger',
      'nav-item--mobile': props.mobile,
      'nav-item--static': props.static
    }"
    @click="handleClick"
  >
    <div class="nav-item-content">
      <div class="nav-item-left">
        <v-progress-circular
          v-if="loading"
          indeterminate
          :size="props.mobile ? 20 : 16"
          :width="2"
          class="nav-item-loader"
        />
        <Icon v-else-if="icon" :name="icon" :size="props.mobile ? 20 : 16" :interactive="false" />
        <span>{{ title }}</span>
      </div>
      <slot name="trailing">
        <span v-if="suffix" class="nav-item-suffix">{{ suffix }}</span>
      </slot>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import Icon from '@/components/icons/Icon.vue'
import { VProgressCircular } from 'vuetify/components/VProgressCircular'

interface Props {
  title: string
  route?: RouteLocationRaw
  icon?: string
  params?: Record<string, string>
  type?: 'default' | 'danger'
  active?: boolean
  suffix?: string
  mobile?: boolean
  // Spinner à la place de l'icône (action en cours)
  loading?: boolean
  // Ligne non cliquable (div) : le slot trailing porte alors les contrôles
  static?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'default',
  active: false,
  mobile: false,
  loading: false,
  static: false
})

const emit = defineEmits<{
  (e: 'select'): void
}>()

const route = useRoute()

const componentProps = computed(() => {
  if (props.route) {
    return {
      to: typeof props.route === 'string' 
        ? { name: props.route, params: props.params }
        : props.route
    }
  }
  // Bouton natif : type explicite pour ne jamais soumettre un formulaire englobant
  return props.static ? {} : { type: 'button' }
})

// Une ligne statique porte ses propres contrôles (slot trailing) et n'émet rien
const handleClick = () => {
  if (!props.static) emit('select')
}

const isCurrentRoute = computed(() => {
  if (props.active) return true
  if (!props.route) return false
  if (typeof props.route === 'string') {
    return route.name === props.route
  }
  return route.name === (props.route as { name?: string }).name
})
</script>

<style scoped>
.nav-item {
  cursor: pointer;
  display: flex;
  padding: 6px 16px 6px 12px;
  align-items: center;
  gap: 12px;
  align-self: stretch;
  border-radius: 8px;
  color: var(--text-defaut);
  font-family: Outfit;
  font-size: 15px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.1px;
  text-decoration: none;
  transition: all 0.2s ease;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
}

.nav-item:hover {
  background: var(--layout-surface-variant-low);
}

.nav-item.active {
  background: var(--state-selected-bg);
  color: var(--state-selected-text);
  font-weight: 700;
}

.nav-item.active :deep(svg) {
  color: var(--state-selected-text);
}

.nav-item--danger {
  color: var(--text-danger);
}

.nav-item--danger :deep(svg) {
  color: var(--text-danger);
}

.nav-item--danger:hover {
  background: rgba(233, 57, 57, 0.08);
}

.nav-item--mobile {
  padding: 10px 16px;
  min-height: 44px;
  font-size: 16px;
}

.nav-item--static {
  cursor: default;
}

.nav-item--static:hover {
  background: none;
}

.nav-item-loader {
  color: var(--components-icon-color);
  flex-shrink: 0;
}

.nav-item--mobile .nav-item-left{
  gap: 12px;
}

.nav-item-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.nav-item-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-item-suffix {
  color: var(--text-placeholder);
  font-family: Outfit;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

.nav-item.active .nav-item-suffix {
  color: var(--state-selected-text);
}
</style>
